import React, { useMemo } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@shadcn/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@shadcn/components/ui/table";


const WeeklySalesTable = ({ salesData, loading, searchTerm }) => {
  
  
  
  const filteredTableData = useMemo(() => {
    if (!salesData?.data) return [];
    
    let data = Object.entries(salesData.data);
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      data = data.filter(([sku]) => 
        sku.toLowerCase().includes(searchLower)
      );
    }
    
    
    return data.sort((a, b) => {
      const totalA = Object.values(a[1]).reduce((sum, val) => 
        typeof val === 'number' ? sum + val : sum, 0) - (a[1].mtd || 0);
      const totalB = Object.values(b[1]).reduce((sum, val) => 
        typeof val === 'number' ? sum + val : sum, 0) - (b[1].mtd || 0);
      return totalB - totalA;
    });
  }, [salesData, searchTerm]);

  
  const totalUnitsSold = useMemo(() => {
    return filteredTableData.reduce((total, [sku, values]) => {
      const skuTotal = Object.values(values).reduce((sum, val) => 
        typeof val === 'number' ? sum + val : sum, 0) - (values.mtd || 0);
      return total + skuTotal;
    }, 0);
  }, [filteredTableData]);

  if (loading && !salesData) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex justify-center items-center h-64">
              <div className="flex flex-col items-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                <span className="mt-2 text-sm text-gray-500">Loading sales data...</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Total Units Summary */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Units Sold</h3>
            <div className="text-4xl font-bold text-blue-600">
              {totalUnitsSold.toLocaleString()}
            </div>
            <p className="text-sm text-gray-600 mt-2">
              From {filteredTableData.length} SKU{filteredTableData.length !== 1 ? 's' : ''}
              {searchTerm && (
                <span className="ml-1 text-blue-600">
                  (filtered by "{searchTerm}")
                </span>
              )}
            </p>
          </div>
        </CardContent>
      </Card>

      
      {/* Table - SKU Sales */}
      <Card>
        <CardHeader>
          <CardTitle>
            SKU Sales
            {salesData?.reportPeriod && (
              <span className="ml-2 text-sm font-normal">
                ({salesData.reportPeriod.start} to {salesData.reportPeriod.end})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Horizontal scroll container with improved styling */}
          <div className="relative">
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
                <div className="flex flex-col items-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                  <span className="mt-2 text-sm text-gray-500">Loading table data...</span>
                </div>
              </div>
            )}
            
            {/* Scroll container with shadow indicators */}
            <div className="overflow-x-auto shadow-inner rounded-lg border">
              <div className="w-full inline-block align-middle">
                <Table className="w-full">
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="sticky left-0 bg-gray-50 z-10 w-[180px] min-w-[180px] max-w-[180px] border-r border-gray-200">
                        <div className="font-semibold">SKU</div>
                      </TableHead>
                      {salesData?.weekInfo && Object.entries(salesData.weekInfo).map(([weekKey, weekData]) => (
                        <TableHead key={weekKey} className="text-center min-w-[120px] px-3">
                          <div className="font-semibold">{`WEEK ${weekKey.replace('week', '')}`}</div>
                          <div className="text-xs font-normal text-gray-500 mt-1">
                            {weekData.start} - {weekData.end}
                          </div>
                        </TableHead>
                      ))}
                      <TableHead className="text-center min-w-[100px] bg-blue-50 font-semibold">
                        Total
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTableData.map(([sku, values]) => {
                      const total = Object.values(values).reduce((sum, val) => 
                        typeof val === 'number' ? sum + val : sum, 0) - (values.mtd || 0);
                      
                      return (
                        <TableRow key={sku} className="hover:bg-gray-50">
                          <TableCell className="sticky left-0 bg-white z-10 border-r border-gray-200 w-[180px] min-w-[180px] max-w-[180px]">
                            <div 
                              className="font-medium text-sm truncate" 
                              title={sku}
                              style={{ 
                                maxWidth: '160px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {sku}
                            </div>
                          </TableCell>
                          {salesData.weekInfo && Object.keys(salesData.weekInfo).map(weekKey => (
                            <TableCell key={weekKey} className="text-center px-3">
                              <span className="text-sm">
                                {values[weekKey] ? values[weekKey].toLocaleString() : 0}
                              </span>
                            </TableCell>
                          ))}
                          <TableCell className="text-center font-bold bg-blue-50">
                            <span className="text-sm text-blue-700">
                              {total.toLocaleString()}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
            
            {/* Scroll hint for mobile */}
            {salesData?.weekInfo && Object.keys(salesData.weekInfo).length > 3 && (
              <div className="text-center mt-3 text-sm text-gray-500 md:hidden">
                <span>← Scroll horizontally to view all weeks →</span>
              </div>
            )}
            
            {/* No data message */}
            {filteredTableData.length === 0 && !loading && (
              <div className="text-center py-12 text-gray-500">
                <div className="text-lg font-medium mb-2">No data found</div>
                <div className="text-sm">
                  {searchTerm ? `No SKUs found matching "${searchTerm}"` : 'No sales data available for the selected period'}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WeeklySalesTable; 
